import { Link, LinkProps } from 'react-router-dom'

import { isV2SupportDeprecatedPath, V2_SUPPORT_DEPRECATED_FLAG, V3_URL } from '@/constants/featureFlags'
import { useFeatureFlagsContext } from '@/context'

/**
 * Drop-in replacement for react-router's `<Link>`. Same rule as
 * `useGuardedNavigate`, for declarative links instead of imperative
 * navigation: when `v2SupportDeprecated` is on and `to` falls under the
 * deprecated V2 "support" surface, the link points at Mission Control V3.
 *
 * V3 is a different origin, so that case renders a plain `<a>` — `<Link>`
 * would treat the absolute URL as an in-app route and produce a broken
 * relative href.
 */
export const GuardedLink = ({ to, children, ...rest }: LinkProps) => {
  const { isEnabled } = useFeatureFlagsContext()
  const supportDeprecated = isEnabled(V2_SUPPORT_DEPRECATED_FLAG)

  if (supportDeprecated && typeof to === 'string' && isV2SupportDeprecatedPath(to)) {
    // Router-only props have no meaning on a plain anchor, and React would warn
    // about them as unknown DOM attributes. Strip every non-anchor prop in
    // LinkProps, not just the ones current callers happen to pass.
    const {
      preventScrollReset: _preventScrollReset,
      relative: _relative,
      reloadDocument: _reloadDocument,
      replace: _replace,
      state: _state,
      viewTransition: _viewTransition,
      ...anchorProps
    } = rest

    return (
      <a {...anchorProps} href={V3_URL}>
        {children}
      </a>
    )
  }

  return (
    <Link {...rest} to={to}>
      {children}
    </Link>
  )
}
